import React, { useState } from 'react';
import { func } from 'prop-types';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { FormattedMessage } from '../../util/reactIntl';
import { getCategories } from '../../config/runtimeListingConfig';
import { getCategoryUrl } from '../../util/categoryLandingHelpers';

import css from './BrowseMenu.module.css';

const EMPTY_COLLAPSE_THRESHOLD = 4;

/**
 * Category drill-down browse (prototype "Browse" view, phone/tablet): back
 * row, current-category heading, "View all N listings →", then child rows
 * with rolled-up counts and a chevron for branches. Empty categories grey
 * out with "none yet" and collapse behind a "N more with nothing listed
 * yet" row when there are many. Leaf taps navigate to the SEO'd category
 * landing page.
 */
const BrowseMenu = props => {
  const { onSelect } = props;
  const history = useHistory();
  const [path, setPath] = useState([]);
  const [showEmpties, setShowEmpties] = useState(false);
  // Asked for as the menu opens (browse.duck fetchCategoryCounts); null while unknown,
  // and the rows then show no counts or greying
  const counts = useSelector(state => state.browse.categoryCounts);

  const countOf = category => (counts ? counts[category.id] || 0 : null);

  const current = path[path.length - 1] || null;
  const kids = current ? current.subcategories || [] : getCategories();

  const navigateTo = category => {
    if (onSelect) onSelect();
    history.push(getCategoryUrl(category.id));
  };

  const rowFor = category => {
    const hasKids = (category.subcategories || []).length > 0;
    const count = countOf(category);
    const isEmpty = count === 0;
    return (
      <button
        type="button"
        key={category.id}
        className={classNames(css.row, { [css.rowEmpty]: isEmpty })}
        onClick={() => (hasKids ? setPath([...path, category]) : navigateTo(category))}
      >
        <span className={css.rowName}>{category.name}</span>
        <span className={css.rowMeta}>
          {count == null ? null : isEmpty ? <FormattedMessage id="BrowseMenu.noneYet" /> : count}
          {hasKids ? <span className={css.chevron}>›</span> : null}
        </span>
      </button>
    );
  };

  const populated = counts ? kids.filter(k => countOf(k) > 0) : kids;
  const empties = counts ? kids.filter(k => countOf(k) === 0) : [];
  const showAllEmpties = empties.length <= EMPTY_COLLAPSE_THRESHOLD || showEmpties;

  const currentCount = current ? countOf(current) : null;

  return (
    <nav className={css.root}>
      {path.length > 0 ? (
        <button
          type="button"
          className={css.backRow}
          onClick={() => {
            setPath(path.slice(0, -1));
            setShowEmpties(false);
          }}
        >
          ‹{' '}
          {path.length > 1 ? (
            path[path.length - 2].name
          ) : (
            <FormattedMessage id="BrowseMenu.allCategories" />
          )}
        </button>
      ) : null}

      <h2 className={css.heading}>
        {current ? current.name : <FormattedMessage id="BrowseMenu.browseAll" />}
      </h2>

      {/* A category's own page, unless it is known to be empty: while the counts load, or
          when they couldn't be had, without a number */}
      {current && currentCount !== 0 ? (
        <button type="button" className={css.viewAll} onClick={() => navigateTo(current)}>
          {currentCount == null ? (
            <FormattedMessage id="BrowseMenu.viewAllNoCount" />
          ) : (
            <FormattedMessage id="BrowseMenu.viewAll" values={{ count: currentCount }} />
          )}
        </button>
      ) : null}

      <div className={css.rows}>
        {populated.map(rowFor)}
        {showAllEmpties ? (
          <>
            {empties.map(rowFor)}
            {empties.length > EMPTY_COLLAPSE_THRESHOLD ? (
              <button
                type="button"
                className={css.toggleEmpties}
                onClick={() => setShowEmpties(false)}
              >
                <FormattedMessage id="BrowseMenu.hideEmpty" />
              </button>
            ) : null}
          </>
        ) : empties.length > 0 ? (
          <button type="button" className={css.toggleEmpties} onClick={() => setShowEmpties(true)}>
            <FormattedMessage id="BrowseMenu.moreEmpty" values={{ count: empties.length }} />
          </button>
        ) : null}
      </div>
    </nav>
  );
};

BrowseMenu.defaultProps = {
  onSelect: null,
};

BrowseMenu.propTypes = {
  onSelect: func,
};

export default BrowseMenu;
